import type { ChiTietPhieuXuatKho, PhieuXuatKhoAmount } from '@/types/schemas';
import type { FileDef } from '@/lib/excel/export/types';
import { save } from '@/lib/excel/export';
import { toast } from '@/hooks/use-toast';

type PhieuXuatKhoDetailExportRow = ChiTietPhieuXuatKho & { exportLine: number };

const toNumber = (value: PhieuXuatKhoAmount | null | undefined) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const textValue = (value: unknown) => (typeof value === 'string' ? value : '');

const buildDetailExportName = (documentNumber?: string) => {
  const safeDocumentNumber = documentNumber
    ?.trim()
    .replace(/[<>:"/\\|?*]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return safeDocumentNumber ? `ChiTietPhieuXuatKho-${safeDocumentNumber}` : 'ChiTietPhieuXuatKho';
};

const displayedAmount = (row: ChiTietPhieuXuatKho) => {
  if (row.tien_nt !== null && row.tien_nt !== undefined && row.tien_nt !== '') return toNumber(row.tien_nt);
  return toNumber(row.gia_nt) * toNumber(row.so_luong);
};

export const PHIEU_XUAT_KHO_DETAIL_FILE: FileDef<PhieuXuatKhoDetailExportRow> = {
  name: 'ChiTietPhieuXuatKho',
  sheet: 'Chi tiết phiếu xuất kho',
  title: 'CHI TIẾT PHIẾU XUẤT KHO',
  cols: [
    { header: 'Dòng', width: 8, get: row => row.exportLine },
    { header: 'Mã sản phẩm', width: 20, get: row => row.ma_vt_data?.ma_vt || textValue(row.ma_vt) },
    { header: 'Tên vật tư', width: 35, get: row => row.ma_vt_data?.ten_vt || row.ten_vt || '' },
    {
      header: 'Đvt',
      width: 12,
      get: row =>
        (typeof row.dvt === 'object' ? row.dvt?.dvt : row.dvt) ||
        row.dvt_data?.dvt ||
        row.ma_vt_data?.dvt_data?.dvt ||
        row.ma_vt_data?.dvt ||
        ''
    },
    {
      header: 'Mã kho',
      width: 16,
      get: row => row.ma_kho_data?.ma_kho || row.ma_vt_data?.ma_kho_data?.ma_kho || textValue(row.ma_kho)
    },
    { header: 'Mã vị trí', width: 16, get: row => row.ma_vi_tri_data?.ma_vi_tri || textValue(row.ma_vi_tri) },
    { header: 'Tồn', width: 14, get: row => toNumber(row.sl_ton ?? row.ton_kho) },
    { header: 'Số lượng', width: 14, get: row => toNumber(row.so_luong) },
    { header: 'Đích danh', width: 12, get: row => Boolean(row.px_dd) },
    { header: 'Giá VND', width: 16, get: row => toNumber(row.gia_nt) },
    { header: 'Tiền VND', width: 18, get: displayedAmount },
    {
      header: 'Tk có',
      width: 12,
      get: row => row.tk_vt_data?.code || row.ma_vt_data?.tk_khcp_data?.code || textValue(row.tk_vt)
    },
    { header: 'Lý do xuất', width: 16, get: row => row.ma_nx_data?.ma_nx || textValue(row.ma_nx) },
    { header: 'Tk nợ', width: 12, get: row => row.tk_du_data?.code || textValue(row.tk_du) },
    { header: 'Bộ phận', width: 16, get: row => row.ma_bp_data?.ma_bp || textValue(row.ma_bp) },
    {
      header: 'Vụ việc',
      width: 16,
      get: row => row.ma_vv_data?.ma_vu_viec || row.ma_vv_data?.ma_vv || textValue(row.ma_vv)
    },
    { header: 'Đợt thanh toán', width: 18, get: row => row.ma_dtt_data?.ma_dtt || textValue(row.ma_dtt) },
    { header: 'Khế ước', width: 16, get: row => row.ma_ku_data?.ma_ku || textValue(row.ma_ku) },
    { header: 'Phí', width: 14, get: row => row.ma_phi_data?.ma_phi || textValue(row.ma_phi) },
    { header: 'Sản phẩm', width: 18, get: row => row.ma_sp_data?.ma_vt || textValue(row.ma_sp) },
    {
      header: 'Lệnh sản xuất',
      width: 20,
      get: row => row.ma_lsx_data?.so_ct || row.ma_lsx_data?.ma_lsx || textValue(row.ma_lsx)
    },
    {
      header: 'C/p không h/lệ',
      width: 18,
      get: row => row.ma_cp0_data?.ma_cpkhl || row.ma_cp0_data?.ma_cp || textValue(row.ma_cp0)
    },
    { header: 'Số phiếu YC', width: 18, get: row => row.so_phieu_yc || '' },
    { header: 'Dòng YC', width: 12, get: row => row.dong_yc || row.line_yc || '' }
  ]
};

export const exportPhieuXuatKhoDetailsToExcel = async (rows: ChiTietPhieuXuatKho[], documentNumber?: string) => {
  if (rows.length === 0) {
    toast({ description: 'Không có dữ liệu chi tiết để kết xuất.' });
    return;
  }

  try {
    const exportRows = rows.map((row, index) => ({ ...row, exportLine: index + 1 }));
    await save(exportRows, {
      ...PHIEU_XUAT_KHO_DETAIL_FILE,
      name: buildDetailExportName(documentNumber)
    });
    toast({ description: 'Kết xuất chi tiết phiếu xuất kho thành công.' });
  } catch (error) {
    console.error('Export warehouse issue details error:', error);
    toast({ description: 'Không thể kết xuất chi tiết phiếu xuất kho.', variant: 'destructive' });
  }
};
